import Model, { attr, belongsTo } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import { service } from '@ember/service'
import type SchoolSubscription from 'teacher-ui-client/models/school-subscription'
import type TeacherSubscription from 'teacher-ui-client/models/teacher-subscription'
import type { IntlService } from 'ember-intl'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type StringTransform from 'teacher-ui-client/transforms/string'
import type NumberTransform from 'teacher-ui-client/transforms/number'

export const LICENCE_COUNT_UNLIMITED = 0
export const REMAINING_LICENCES_UNLIMITED = 100000

/**
 * @class SubscriptionUtilisationModel
 *
 * When querying, you will receive exactly 1 utilisation record for each subscription type that has a current/valid/active
 * subscription. If there is no current/valid/active for a subscription type, you will not receive a utilisation record
 * for it.
 *
 * You may receive 'reading', 'maths', and/or 'wf' utilisation records.
 *
 * If multiple active school subscriptions for a subscription type, the licence counts are consolidated - the
 * utilisation record for the subscription type will return a licence count that adds up the licenses from the active
 * school subscriptions. However, if an active teacher subscription is present for the user for the subscription type,
 * that is used _instead_ of school sub counts. Multiple active teacher subs are consolidated in the same way for the
 * utilisation counts.
 *
 * Example: (for a single subscription type)
 * - active school sub: 10 licences
 * - active school sub: 5 licences
 * - utilisation record licence count: 15 licenses
 *
 * if an active teacher sub is present as well;
 * - active school sub: 10 licences
 * - active school sub: 5 licences
 * - active teacher sub: 3 licenses
 * - utilisation record licence count: 3 licenses
 */
export default class SubscriptionUtilisation extends Model {
  // the record belongs to either a teacher-subscription or a school-subscription
  @belongsTo<TeacherSubscription>('teacher-subscription', { async: false, inverse: 'subscriptionUtilisation' })
  declare teacherSubscription?: TeacherSubscription

  @belongsTo<SchoolSubscription>('school-subscription', { async: false, inverse: 'subscriptionUtilisation' })
  declare schoolSubscription?: SchoolSubscription

  // the total licence capacity of the subscription (0 if unlimited)
  @attr<NumberTransform>('number') declare licenceCount: number

  // remaining licences = licence count - student count (except for unlimited, where it always == 100,000)
  @attr<NumberTransform>('number') declare remainingLicences: number

  // the number of active licences - ie active students
  @attr<NumberTransform>('number') declare studentCount: number

  @attr<StringTransform>('string') declare subscriptionType: SubscriptionType

  @service intl!: IntlService

  get isUnlimitedCapacity() {
    // Ensure both - If it's unlimited, it shouldn't ever be one or the other.
    return this.licenceCount === LICENCE_COUNT_UNLIMITED && this.remainingLicences === REMAINING_LICENCES_UNLIMITED
  }

  get isAtCapacity() {
    return this.remainingLicences === 0
  }

  get isOverCapacity() {
    return this.remainingLicences < 0
  }

  // sometimes want to show warnings when they're at capacity, not just over it
  get isAtOrOverCapacity() {
    return this.isAtCapacity || this.isOverCapacity
  }

  get isUnderCapacity() {
    return this.isUnlimitedCapacity || !this.isAtOrOverCapacity
  }

  /**
   * Check to see if a positive number of students count be added to the subscription without exceeding capacity
   *
   * @param {Number} studentCount - count of students (must be >= 0 )
   * @returns {Boolean} - true if capacity would remain under or at capacity
   */
  hasCapacityFor(studentCount: number) {
    if (this.isUnlimitedCapacity) {
      return true
    } else if (this.isUnderCapacity) {
      return this.remainingLicences >= studentCount
    } else {
      // At or over capacity, there is still room for zero students
      return studentCount === 0
    }
  }

  /**
   * Return string value indicating the total capacity size (regardless of usage). For display only.
   *
   * Don't do any logic based on this value. You should use the computed properties above that return booleans to
   * determine when to show warnings/icons/modals etc, then use this value for display when needed.
   */
  get capacitySize() {
    return this.isUnlimitedCapacity ? this.intl.t('unlimited') : String(this.licenceCount)
  }

  /**
   * Return string value indicating a positive number that the capacity is exceeded by. Only useful if utilisation has a
   * defined capacity and is over that capacity. For display only.
   *
   * Don't do any logic based on this value. You should use the computed properties above that return booleans to
   * determine when to show warnings/icons/modals etc, then use this value for display when needed.
   *
   * @returns {String} - some positive number as string, indicating how much capacity has been exceeded by
   */
  get capacityExceededBy() {
    if (this.isAtOrOverCapacity) {
      return String(Math.abs(this.remainingLicences))
    } else {
      return '0'
    }
  }

  [Type] = 'subscription-utilisation' as const
}
